import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, filter, firstValueFrom, take } from 'rxjs';

import { UserIdService } from './user-id.service';
import { SettingsService } from './settings.service';
import { MfaService } from './mfa.service';
import { Settings } from '../models/settings.model';
import { MfaPromptModalComponent } from '../components/mfa-prompt-modal/mfa-prompt-modal.component';
import { MfaEnrollmentModalComponent } from '../components/mfa-enrollment-modal/mfa-enrollment-modal.component';

/**
 * Shows a soft nudge encouraging non-enrolled users to set up 2FA. Scheduled
 * enforcement reminders show once per local day; the generic security
 * suggestion is gated behind the public `settings/current.mfaPromptEnabled`
 * flag (off unless set) and remains one-time.
 *
 * Once a user is actively subject to enforcement, they are handled by the
 * grace-period banner and enforcement gate instead.
 */
@Injectable({ providedIn: 'root' })
export class MfaPromptService {
  private hasInitialised = false;
  private subscription?: Subscription;
  private currentUserId?: string;
  private inFlightUsers = new Set<string>();
  private handledPromptKeys = new Set<string>();
  private nextDailyCheckHandle?: ReturnType<typeof setTimeout>;
  private modalRef?: NgbModalRef;

  constructor(
    private auth: Auth,
    private userIdService: UserIdService,
    private settingsService: SettingsService,
    private mfaService: MfaService,
    private modalService: NgbModal,
  ) {}

  init(): void {
    if (this.hasInitialised) {
      return;
    }

    this.hasInitialised = true;
    this.subscription = this.userIdService.get$().subscribe((userId) => {
      if (!userId) {
        this.currentUserId = undefined;
        this.inFlightUsers.clear();
        this.handledPromptKeys.clear();
        this.clearDailyCheck();
        this.modalRef?.close();
        this.modalRef = undefined;
        return;
      }

      this.currentUserId = userId;

      if (this.modalRef || this.inFlightUsers.has(userId)) {
        return;
      }

      void this.maybePrompt(userId);
    });
  }

  private genericSeenKey(userId: string): string {
    return `mfa-prompt-seen:${userId}`;
  }

  private scheduledSeenKey(userId: string): string {
    return `mfa-required-prompt-last-shown:${userId}`;
  }

  private currentLocalDateKey(date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private hasSeenGenericPrompt(userId: string): boolean {
    const promptKey = `generic:${userId}`;
    if (this.handledPromptKeys.has(promptKey)) {
      return true;
    }

    try {
      return localStorage.getItem(this.genericSeenKey(userId)) === '1';
    } catch {
      return false;
    }
  }

  private persistGenericPromptSeen(userId: string): void {
    this.handledPromptKeys.add(`generic:${userId}`);
    try {
      localStorage.setItem(this.genericSeenKey(userId), '1');
    } catch {
      // Ignore storage failures (private mode etc.); the in-memory set still
      // prevents re-prompting within this session.
    }
  }

  private hasSeenScheduledPromptToday(userId: string, dateKey: string): boolean {
    const promptKey = `scheduled:${userId}:${dateKey}`;
    if (this.handledPromptKeys.has(promptKey)) {
      return true;
    }

    try {
      return localStorage.getItem(this.scheduledSeenKey(userId)) === dateKey;
    } catch {
      return false;
    }
  }

  private persistScheduledPromptSeen(userId: string, dateKey: string): void {
    this.handledPromptKeys.add(`scheduled:${userId}:${dateKey}`);
    try {
      localStorage.setItem(this.scheduledSeenKey(userId), dateKey);
    } catch {
      // The in-memory key still prevents duplicate prompts during this visit.
    }
  }

  private async maybePrompt(userId: string): Promise<void> {
    if (this.inFlightUsers.has(userId)) {
      return;
    }

    this.inFlightUsers.add(userId);
    let recheckScheduledPrompt = false;

    try {
      // Wait until the public settings doc has loaded. The generic prompt still
      // respects its toggle; a scheduled enforcement reminder is part of the
      // rollout itself and should not be suppressed by that optional flag.
      const settings = await firstValueFrom(
        this.settingsService.get$().pipe(filter((s): s is Settings => !!s), take(1)),
      ).catch(() => undefined);

      // Re-validate after awaits: same user, correct auth, not already open.
      if (this.currentUserId !== userId || this.auth.currentUser?.uid !== userId || this.modalRef) {
        return;
      }

      if (this.mfaService.isEnrolled()) {
        return;
      }

      // Eligibility is decided from the enforcement state rather than from whether
      // the user holds a privileged role, so a group admin whose cohort has a start
      // date in the future is nudged to enrol ahead of it instead of being left
      // until the deadline. Two groups are still skipped:
      //   - `required`: already subject, so they get the grace pop-up and banner.
      //   - `satisfiedBySso`: covered by trusted-domain Google SSO already.
      const state = await this.mfaService.refreshStatus();
      if (state?.required || state?.satisfiedBySso) {
        return;
      }

      if (this.currentUserId !== userId || this.modalRef) {
        return;
      }

      const isScheduled = typeof state?.requiredFrom === 'number';
      const dateKey = this.currentLocalDateKey();

      if (isScheduled) {
        if (this.hasSeenScheduledPromptToday(userId, dateKey)) {
          this.scheduleNextDailyCheck(userId);
          return;
        }
      } else {
        this.clearDailyCheck();

        if (!settings?.mfaPromptEnabled || this.hasSeenGenericPrompt(userId)) {
          return;
        }
      }

      const ref = this.modalService.open(MfaPromptModalComponent, {
        centered: true,
        size: 'sm',
        backdrop: 'static',
        keyboard: false,
      });
      // When enforcement is already scheduled for this user's cohort, the nudge
      // names the date instead of making a generic security suggestion.
      ref.componentInstance.requiredFrom = state?.requiredFrom ?? null;
      this.modalRef = ref;

      let wantsSetup = false;
      try {
        wantsSetup = await ref.result;
      } catch {
        wantsSetup = false;
      } finally {
        if (this.modalRef === ref) {
          this.modalRef = undefined;
        }
      }

      if (wantsSetup) {
        const enrollmentRef = this.modalService.open(
          MfaEnrollmentModalComponent,
          { centered: true, backdrop: 'static', keyboard: false, size: 'md' },
        );
        if (isScheduled) {
          enrollmentRef.componentInstance.dismissLabel = 'Remind me later';
          enrollmentRef.componentInstance.showCloseButton = false;
        }
        const completed = await enrollmentRef.result.catch(() => false);
        recheckScheduledPrompt = !completed;
      } else if (isScheduled) {
        this.persistScheduledPromptSeen(userId, dateKey);
        this.scheduleNextDailyCheck(userId);
      } else {
        this.persistGenericPromptSeen(userId);
      }
    } finally {
      this.inFlightUsers.delete(userId);
      if (recheckScheduledPrompt && this.currentUserId === userId && !this.modalRef) {
        void this.maybePrompt(userId);
      }
    }
  }

  private scheduleNextDailyCheck(userId: string): void {
    this.clearDailyCheck();

    if (this.currentUserId !== userId) {
      return;
    }

    const now = new Date();
    const nextDay = new Date(now);
    nextDay.setHours(24, 0, 1, 0);
    const delay = Math.max(1000, nextDay.getTime() - now.getTime());

    this.nextDailyCheckHandle = setTimeout(() => {
      this.nextDailyCheckHandle = undefined;
      if (this.currentUserId === userId && !this.modalRef) {
        void this.maybePrompt(userId);
      }
    }, delay);
  }

  private clearDailyCheck(): void {
    if (this.nextDailyCheckHandle) {
      clearTimeout(this.nextDailyCheckHandle);
      this.nextDailyCheckHandle = undefined;
    }
  }
}
